import { flexRender } from "@tanstack/react-table";
import { format } from "date-fns";

// ─── Shared Types ───────────────────────────────────────────────────────────

export interface DynamicData {
  loaders: { [key: string]: boolean };
  [key: string]: any;
}

export interface TableProps {
  columns: any[];
  title: string;
  fetchData: any;
  type?: string;
  applicationStatusData?: (id: any) => void;
  has_drilldown?: boolean;
  drill_down_chart_id?: string | number;
  /** Chart id for taluk → village drilldown. Falls back to drill_down_chart_id. */
  drill_down_level3_chart_id?: string | number;
  fetchPopupData?: (row: any, type: string) => void;
  id?: string | number;
  getSubData?: (row: any) => Promise<any[]>;
}

export interface Level2TableProps {
  selectorData: any;
  level2ChartId: string | number | undefined;
  level3ChartId: string | number | undefined;
  subData: any[];
  dispatch: any;
  id: any;
  applicationStatusData?: (id: any) => void;
  fetchPopupData?: (row: any, type: string) => void;
  globalFilter: string;
}

export interface Level3TableProps {
  selectorData: any;
  chartId: string | number | undefined;
  dispatch: any;
  id: any;
  fetchPopupData?: (row: any, type: string) => void;
}

// ─── Formatting Helpers ─────────────────────────────────────────────────────

export const formatDateTime = (date: string) =>
  format(new Date(date), "dd/MM/yyyy HH:mm");

export const getStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    Approved: "bg-green-100 text-green-700 border border-green-500",
    Pending: "bg-yellow-100 text-yellow-700 border border-yellow-500",
    Rejected: "bg-red-100 text-red-700 border border-red-500",
  };
  return colors[status] || "bg-gray-100 text-gray-700 border border-gray-500";
};

export const formatNumberWithCommas = (value: any) => {
  const locale = "en-IN";
  if (value === null || value === undefined || value === "") return "0";
  if (typeof value === "string") {
    const cleaned = value.replace(/,/g, "");
    if (!isNaN(Number(cleaned))) return Number(cleaned).toLocaleString(locale);
    return value;
  }
  if (typeof value === "number") return value.toLocaleString(locale);
  return value;
};

/** Render a cell value with proper formatting (date / status / number / plain). */
export const renderCellValue = (cell: any) => {
  if (cell.column.id === "created_at") {
    return formatDateTime(cell.getValue() as string);
  }
  if (cell.column.id === "status") {
    const raw = cell.getValue() as string;
    // Returns JSX — callers should handle this in a React context
    return null; // Handled inline in components that need the <span>
  }
  // District/taluk/village names are identifiers, not counts - a null name
  // should render blank, not "0".
  if (["district_name", "taluk_name", "village_name"].includes(cell.column.id)) {
    const rawValue = cell.getValue();
    return rawValue == null ? "" : rawValue;
  }
  const rawValue = cell.getValue();
  if (
    typeof rawValue === "number" ||
    rawValue == null ||
    (typeof rawValue === "string" &&
      /^-?\d+(?:\.\d+)?$/.test((rawValue as string).replace(/,/g, "")))
  ) {
    return formatNumberWithCommas(rawValue);
  }
  return flexRender(cell.column.columnDef.cell, cell.getContext());
};

// ─── Column Helpers ─────────────────────────────────────────────────────────

/** Removes column groups where every row has zero / null for that field. */
export const removeZeroColumns = (data: any[], columns: any[]) => {
  if (!Array.isArray(data) || data.length === 0) return columns;

  const isZeroValue = (v: any) =>
    v === 0 || v === "0" || v === null || v === undefined || v === "" || v === "null";

  const processColumns = (cols: any[]): any[] =>
    cols
      .map((col) => {
        if (col.columns) {
          const processedChildren = processColumns(col.columns);
          if (processedChildren.length === 0) return null;
          return { ...col, columns: processedChildren };
        }
        if (col.field) {
          const allZero = data?.every((row) => isZeroValue(row[col.field]));
          return allZero ? null : col;
        }
        return col;
      })
      .filter((col) => col !== null);

  return processColumns(columns);
};

/** Convert raw API column config to @tanstack/react-table format. */
export const convertColumns = (cols: any[]): any[] =>
  (cols || []).map((col) => {
    if (col.columns)
      return { header: col.header, columns: convertColumns(col.columns) };
    return { accessorKey: col.field, header: col.header };
  });
